import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { surveySections, surveyCategory, surveyList, surveyCategoryCollapsed } from "./survey-session-question-list.module.scss";
import { ISurveyQuestion } from "../../../models/survey/survey-question.model";
import { ISurveyCategory } from "../../../models/survey/survey-session.model";
import { getAnsweredQuestionsByCategory } from "../utils/survey-session-answers";

import SurveySessionCategoryTitle from "../atoms/survey-session-category-title";
import SurveyAnswerQuestionCard, {
    ISurveyAnswerQuestionCardProps,
} from "../atoms/survey-answer-question-card";
import { updateAnswer } from "../../../redux/survey-session/survey-session.actions";

interface ISurveySessionFragment {
    categories: ISurveyCategory[];
    questions: ISurveyQuestion[] | null;
    canEdit?: boolean;
    sessionId?: number | string;
}

export default function SurveySessionQuestionList({
    categories = [],
    questions = [],
    canEdit = true,
    sessionId,
}: ISurveySessionFragment) {
    const dispatch = useDispatch();
    const [collapsedCategories, setCollapsedCategories] = useState<Set<number>>(new Set());
    const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);

    const questionsByCategory = useMemo(
        () => getAnsweredQuestionsByCategory(questions),
        [questions]
    );

    const answeredCategories = useMemo(
        () =>
            categories.filter(
                ({ categoryId }) => questionsByCategory[categoryId]?.length > 0
            ),
        [categories, questionsByCategory]
    );

    const handleSaveAnswer: ISurveyAnswerQuestionCardProps["onSaveAnswer"] = (
        questionId,
        answer,
        shouldClose = true
    ) => {
        if (sessionId) {
            const payload = {
                [questionId]: answer,
            };
            dispatch(updateAnswer(payload, { sessionId, questionId }));
        }
        
        // Tylko zamykaj pytanie jeśli shouldClose === true
        // Dla automatycznego zapisu (checkbox, radio, select) shouldClose === false
        if (shouldClose) {
            setEditingQuestionId(null);
        }
    };

    const handleStartEdit = (questionId: number) => {
        setEditingQuestionId(questionId);
    };

    const handleCancelEdit = () => {
        setEditingQuestionId(null);
    };

    const toggleCategory = (categoryId: number) => {
        setCollapsedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    return (
        <div className={surveySections}>
            {answeredCategories.map((category) => {
                const categoryQuestions = questionsByCategory[category.categoryId] || [];
                const isCollapsed = collapsedCategories.has(category.categoryId);

                return (
                    <div 
                        key={`survey-category-${category.categoryId}`} 
                        className={`${surveyCategory} ${isCollapsed ? surveyCategoryCollapsed : ""}`}
                    >
                        <SurveySessionCategoryTitle
                            categoryId={category.categoryId}
                            isCollapsed={isCollapsed}
                            onToggle={toggleCategory}
                        >
                            {category.name}
                        </SurveySessionCategoryTitle>
                        <div className={surveyList}>
                            {categoryQuestions.map((question) => (
                                <SurveyAnswerQuestionCard
                                    key={`survey-session-card-${question.questionId}`}
                                    question={question}
                                    canEdit={canEdit}
                                    onSaveAnswer={handleSaveAnswer}
                                    isEditing={editingQuestionId === question.questionId}
                                    onStartEdit={handleStartEdit}
                                    onCancelEdit={handleCancelEdit}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
